import React, {
  createContext,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from 'react'
import { useApolloClient } from '@apollo/client'
import { v4 as uuidv4 } from 'uuid'
import {
  PublishWebEventDocument,
  PublishWebEventInput,
} from '@graphql/react-apollo'
import { EventData, TrackEvent } from '@website/types'
import { useSessionStore } from '@website/stores/sessionStore'

type EventTrackingContextType = {
  trackEvent: TrackEvent
}

export const EventTrackingContext = createContext<
  EventTrackingContextType | undefined
>(undefined)

export const EventTrackingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const apolloClient = useApolloClient()
  const eventQueueRef = useRef<Array<{ eventName: string; data?: EventData }>>(
    [],
  )

  // Process event queue when Apollo Client is ready
  useEffect(() => {
    if (apolloClient && eventQueueRef.current.length > 0) {
      eventQueueRef.current.forEach(({ eventName, data }) => {
        internalTrackEvent(eventName, data)
      })
      eventQueueRef.current = []
    }
  }, [apolloClient])

  // Internal function to track events
  const internalTrackEvent = useCallback(
    (eventName: string, data?: EventData): Promise<void> => {
      // Get the latest session data synchronously
      const { sessionData: currentSessionData } = useSessionStore.getState()

      if (apolloClient) {
        const input: PublishWebEventInput = {
          clientMutationId: uuidv4(),
          event: {
            name: eventName,
            data: {
              ...data,
              ...currentSessionData?.utmParams,
            },
            requestId: uuidv4(),
          },
        }

        return apolloClient
          .mutate({
            mutation: PublishWebEventDocument,
            variables: { input },
          })
          .then(() => Promise.resolve())
          .catch((error: unknown) => {
            console.error('Error publishing web event:', error)
          })
      } else {
        // Queue the event if Apollo Client isn't ready
        eventQueueRef.current.push({ eventName, data })
        return Promise.resolve()
      }
    },
    [apolloClient],
  )

  // Public function to track events
  const trackEvent = useCallback(
    (eventName: string, data?: EventData): Promise<void> => {
      return internalTrackEvent(eventName, data)
    },
    [internalTrackEvent],
  )

  return (
    <EventTrackingContext.Provider value={{ trackEvent }}>
      {children}
    </EventTrackingContext.Provider>
  )
}

export const useEventTracking = () => {
  const context = useContext(EventTrackingContext)
  if (context === undefined) {
    throw new Error(
      'useEventTracking must be used within an EventTrackingProvider',
    )
  }
  return context
}
